// src/pages/Fixtures.jsx
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import styles from "../styles/Fixtures.module.css";
import SiteFooterDisclaimer from "../components/SiteFooterDisclaimer";
import { api } from "../api"; // ← env-based axios client
import { getLeagueCodeFromAny, getLeagueNameFromAny } from "../utils/leagueRouting";
import { prettyCompetitionName } from "../utils/competitionNames";

const FINISHED_STATUSES = ["FT", "AET", "PEN", "FINISHED", "FULLTIME"];
const LIVE_STATUSES = ["1H", "2H", "HT", "ET", "LIVE", "INPLAY", "BREAK", "INT"];

const fixtureStatusMeta = (f) => {
  const fixtureObj = typeof f?.fixture === "object" && f?.fixture ? f.fixture : null;
  const statusObj = typeof f?.status === "object" && f?.status ? f.status : null;
  const nestedStatusObj = typeof fixtureObj?.status === "object" && fixtureObj?.status ? fixtureObj.status : null;
  const raw = String(
    f?.status_short ||
    fixtureObj?.status_short ||
    statusObj?.short ||
    nestedStatusObj?.short ||
    statusObj?.code ||
    nestedStatusObj?.code ||
    f?.match_status ||
    f?.fixture_status ||
    f?.status ||
    ""
  ).toUpperCase();
  const elapsed = Number(
    f?.elapsed ??
    f?.status_elapsed ??
    f?.live_minute ??
    f?.minute ??
    statusObj?.elapsed ??
    nestedStatusObj?.elapsed
  );
  const isFinished = FINISHED_STATUSES.includes(raw);
  const isLive = LIVE_STATUSES.includes(raw) || (!isFinished && Number.isFinite(elapsed) && elapsed > 0);
  return { raw, elapsed, isFinished, isLive };
};

const fixtureScoreMeta = (f) => {
  const fixtureObj = typeof f?.fixture === "object" && f?.fixture ? f.fixture : null;
  const home =
    f?.home_score ??
    f?.goals_home ??
    f?.score_home ??
    f?.score?.home ??
    f?.score?.fulltime?.home ??
    f?.score?.full_time?.home ??
    f?.scores?.home ??
    f?.scores?.fulltime?.home ??
    fixtureObj?.goals?.home ??
    fixtureObj?.score?.home ??
    fixtureObj?.score?.fulltime?.home ??
    f?.goals?.home;
  const away =
    f?.away_score ??
    f?.goals_away ??
    f?.score_away ??
    f?.score?.away ??
    f?.score?.fulltime?.away ??
    f?.score?.full_time?.away ??
    f?.scores?.away ??
    f?.scores?.fulltime?.away ??
    fixtureObj?.goals?.away ??
    fixtureObj?.score?.away ??
    fixtureObj?.score?.fulltime?.away ??
    f?.goals?.away;
  const hasScore = home !== null && home !== undefined && away !== null && away !== undefined;
  return { home, away, hasScore };
};

const fixtureStatusLabel = (f) => {
  const meta = fixtureStatusMeta(f);
  if (meta.isFinished) return "FT";
  if (meta.raw === "HT") return "HT";
  if (meta.isLive) return Number.isFinite(meta.elapsed) ? `${meta.elapsed}'` : "LIVE";
  return null;
};

// Route to the correct detail page by sport (with comp fallback)
const routeFor = (f) => {
  const s = (f.sport || "").toLowerCase();
  const c = (f.comp || "").toLowerCase();
  if (s === "rugby" || s === "rugby union") return `/rugby/match/${f.id}`;
  if (s === "baseball" || c.includes("mlb") || c.includes("baseball")) return `/baseball/game/${f.id}`;
  if (s === "nba" || c === "nba")                          return `/basketball/game/${f.id}`;
  if (s === "nhl" || c === "nhl")                          return `/nhl/game/${f.id}`;
  if (s === "cfb" || c.includes("college") || c === "ncaa") return `/cfb/fixture/${f.id}`;
  if (s === "nfl" || c === "nfl")                          return `/nfl`;
  return `/fixture/${f.id}`;
};

const Fixtures = () => {
  const [fixtures, setFixtures] = useState([]);
  const [loading, setLoading] = useState(true);

  // ---- Helper: format kickoff in user timezone ----
  const formatKickoff = (utcString, withDate = false) => {
    if (!utcString) return "";
    // already has Z or ±HH:MM offset → leave alone; otherwise append Z
    const kickoff = /Z$|[+-]\d{2}:\d{2}$/.test(utcString) ? utcString : utcString + "Z";
    return new Date(kickoff).toLocaleString(navigator.language, {
      weekday: withDate ? "short" : undefined,
      day: withDate ? "numeric" : undefined,
      month: withDate ? "short" : undefined,
      hour: "2-digit",
      minute: "2-digit",
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
  };

  useEffect(() => {
    const fetchFixtures = async () => {
      try {
        // Fetch today + tomorrow to show upcoming fixtures across sports
        const all = [];
        const today = new Date();
        for (let i = 0; i < 2; i++) {
          const d = new Date(today);
          d.setDate(d.getDate() + i);
          const day = d.toISOString().slice(0, 10);
          const { data } = await api.get("/api/public/fixtures/daily", { params: { day, sport: "all" } });
          if (data?.fixtures) all.push(...data.fixtures);
        }
        setFixtures(all);
      } catch (err) {
        console.error("Error fetching fixtures:", err);
        setFixtures([]);
      } finally {
        setLoading(false);
      }
    };
    fetchFixtures();
  }, []);

  if (loading) return <p className={styles.loading}>Loading fixtures...</p>;
  if (!fixtures.length) return <p className={styles.empty}>No fixtures available.</p>;

  // Group fixtures by competition + date
  const grouped = fixtures.reduce((acc, f) => {
    const date = formatKickoff(f.kickoff_utc, true); // includes weekday + date
    const leagueCode = getLeagueCodeFromAny({
      code: f.league_code ?? f.comp_code ?? f.comp,
      name: f.league_name ?? f.comp_name ?? prettyCompetitionName(f.comp, "Football"),
      id: f.league_id,
    });
    const leagueName = prettyCompetitionName(
      getLeagueNameFromAny({
        code: leagueCode,
        name: f.league_name ?? f.comp_name ?? prettyCompetitionName(f.comp, "Football"),
        id: f.league_id,
      }) || f.league_name || f.comp_name || f.comp,
      "Football"
    );

    const key = `${leagueCode || leagueName}__${date}`;
    if (!acc[key]) {
      acc[key] = {
        leagueCode,
        leagueName,
        date,
        fixtures: [],
      };
    }
    acc[key].fixtures.push(f);
    return acc;
  }, {});

  return (
    <div className={styles.page}>
      <h2 className={styles.title}>All Fixtures</h2>
      {Object.entries(grouped).map(([groupKey, group]) => (
        <div key={groupKey} className={styles.group}>
          <h3 className={styles.groupTitle}>
            {group.leagueCode ? (
              <Link to={`/league/${group.leagueCode}`} className={styles.groupLeagueLink}>
                {group.leagueName}
              </Link>
            ) : (
              <span>{group.leagueName}</span>
            )}
            <span className={styles.groupDate}> • {group.date}</span>
          </h3>
          <div className={styles.cards}>
            {group.fixtures.map((f) => (
              <Link key={f.id} to={routeFor(f)} className={styles.card}>
                {(() => {
                  const score = fixtureScoreMeta(f);
                  const statusLabel = fixtureStatusLabel(f);
                  const showScore = score.hasScore && (fixtureStatusMeta(f).isLive || fixtureStatusMeta(f).isFinished);
                  return (
                    <>
                      <div className={styles.teams}>
                        {f.home_team} <span className={styles.vs}>vs</span> {f.away_team}
                      </div>
                      <div className={styles.kickoff}>
                        {showScore ? `${score.home} - ${score.away}` : formatKickoff(f.kickoff_utc)}
                        {statusLabel ? <span style={{ marginLeft: 8, fontWeight: 700 }}>{statusLabel}</span> : null}
                      </div>
                    </>
                  );
                })()}
                {f.sport && f.sport !== "football" && (
                  <div style={{ fontSize: 11, color: "#888", marginTop: 2, textTransform: "uppercase" }}>
                    {f.sport}
                  </div>
                )}
              </Link>
            ))}
          </div>
        </div>
      ))}
      <SiteFooterDisclaimer variant="short" />
    </div>
  );
};

export default Fixtures;
